// src/components/common/aia/AIAvatarContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AIAvatarContext = createContext();

export const useAIAvatar = () => {
    const context = useContext(AIAvatarContext);
    if (!context) {
        throw new Error('useAIAvatar must be used within AIAvatarProvider');
    }
    return context;
};

// Storage keys
const FTUE_PROGRESS_KEY = 'oakhr_ftue_progress';
const FTUE_DISMISSED_KEY = 'oakhr_ftue_dismissed';
const FTUE_RESUME_KEY = 'oakhr_ftue_resume';

export const AIAvatarProvider = ({ children }) => {
    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    // Tour State
    const [currentTour, setCurrentTour] = useState(null);
    const [tourStep, setTourStep] = useState(0);
    const [ftueCompleted, setFtueCompleted] = useState(new Map());
    const [ftueDismissed, setFtueDismissed] = useState(false);

    // User info for role-based tours
    const [userRole, setUserRole] = useState('employee');

    // Load FTUE progress from localStorage on mount
    useEffect(() => {
        const loadProgress = () => {
            try {
                const savedProgress = localStorage.getItem(FTUE_PROGRESS_KEY);
                const dismissed = localStorage.getItem(FTUE_DISMISSED_KEY);

                if (savedProgress) {
                    const parsed = JSON.parse(savedProgress);
                    setFtueCompleted(new Map(Object.entries(parsed)));
                }

                if (dismissed === 'true') {
                    setFtueDismissed(true);
                }
            } catch (error) {
                console.error('Error loading FTUE progress:', error);
            }
        };

        loadProgress();
    }, []);

    // Save FTUE progress to localStorage whenever it changes
    useEffect(() => {
        const saveProgress = () => {
            try {
                const progressObj = Object.fromEntries(ftueCompleted);
                localStorage.setItem(FTUE_PROGRESS_KEY, JSON.stringify(progressObj));
            } catch (error) {
                console.error('Error saving FTUE progress:', error);
            }
        };

        saveProgress();
    }, [ftueCompleted]);

    // Open avatar panel
    const openAvatar = useCallback(() => {
        setIsOpen(true);
        setIsMinimized(false);
    }, []);

    // Close avatar panel
    const closeAvatar = useCallback(() => {
        setIsOpen(false);
        setIsMinimized(false);
    }, []);

    // Toggle avatar panel
    const toggleAvatar = useCallback(() => {
        setIsOpen(prev => !prev);
        setIsMinimized(false);
    }, []);

    // Start a tour
    const startTour = useCallback((tourId, startStep = 0) => {
        setCurrentTour(tourId);
        setTourStep(startStep);
        setIsOpen(true);
        setIsMinimized(false);
    }, []);

    // Go to next tour step
    const nextTourStep = useCallback(() => {
        setTourStep(prev => prev + 1);
    }, []);

    // Jump to a specific tour step
    const goToTourStep = useCallback((stepIndex) => {
        const safeStep = Math.max(0, Number(stepIndex) || 0);
        setTourStep(safeStep);
    }, []);

    // Go to previous tour step
    const previousTourStep = useCallback(() => {
        setTourStep(prev => Math.max(0, prev - 1));
    }, []);

    // Skip current tour (save progress for resume)
    const skipTour = useCallback(() => {
        if (currentTour && tourStep > 0) {
            try {
                localStorage.setItem(FTUE_RESUME_KEY, JSON.stringify({ tourId: currentTour, lastStep: tourStep }));
            } catch (e) { /* ignore */ }
        }
        setCurrentTour(null);
        setTourStep(0);
        setIsOpen(false);
    }, [currentTour, tourStep]);

    // Complete a tour
    const completeTour = useCallback((tourId) => {
        setFtueCompleted(prev => new Map([...prev, [tourId, true]]));
        setCurrentTour(null);
        setTourStep(0);
        setIsOpen(false);
        // Clear resume data on completion
        try { localStorage.removeItem(FTUE_RESUME_KEY); } catch (e) { /* ignore */ }
    }, []);

    // Check if a tour is completed
    const isTourCompleted = useCallback((tourId) => {
        return ftueCompleted.get(tourId) === true;
    }, [ftueCompleted]);

    // Dismiss FTUE banner
    const dismissFTUE = useCallback(() => {
        setFtueDismissed(true);
        localStorage.setItem(FTUE_DISMISSED_KEY, 'true');
    }, []);

    // Reset FTUE (for settings)
    const resetFTUE = useCallback(() => {
        setFtueCompleted(new Map());
        setFtueDismissed(false);
        localStorage.removeItem(FTUE_PROGRESS_KEY);
        localStorage.removeItem(FTUE_DISMISSED_KEY);
    }, []);

    // Check if any FTUE is available for user
    const hasPendingFTUE = useCallback(() => {
        const roleTours = {
            employee: ['employee-welcome-tour'],
            manager: ['manager-welcome-tour'],
            admin: ['admin-welcome-tour']
        };

        const tours = roleTours[userRole] || [];
        return tours.some(tourId => !isTourCompleted(tourId));
    }, [userRole, isTourCompleted]);

    // Get available tours for current user role with status
    const getAvailableTours = useCallback(() => {
        const { getToursByRole } = require('./tours');
        const tours = getToursByRole(userRole);

        return tours.map(tour => ({
            id: tour.id,
            title: tour.title || tour.id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            completed: isTourCompleted(tour.id),
            autoStart: tour.autoStart || false
        }));
    }, [userRole, isTourCompleted]);

    // Get saved resume data for a tour
    const getResumeData = useCallback(() => {
        try {
            const saved = localStorage.getItem(FTUE_RESUME_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch (e) { return null; }
    }, []);

    // Clear resume data
    const clearResumeData = useCallback(() => {
        try { localStorage.removeItem(FTUE_RESUME_KEY); } catch (e) { /* ignore */ }
    }, []);

    // Reset a specific tour for replay
    const resetTour = useCallback((tourId) => {
        setFtueCompleted(prev => {
            const newMap = new Map(prev);
            newMap.delete(tourId);
            return newMap;
        });
    }, []);

    const value = {
        // UI State
        isOpen,
        isMinimized,
        setIsMinimized,

        // Tour State
        currentTour,
        tourStep,
        ftueCompleted,
        ftueDismissed,
        userRole,
        setUserRole,

        // Methods
        openAvatar,
        closeAvatar,
        toggleAvatar,
        startTour,
        nextTourStep,
        goToTourStep,
        previousTourStep,
        skipTour,
        completeTour,
        isTourCompleted,
        dismissFTUE,
        resetFTUE,
        hasPendingFTUE,
        getAvailableTours,
        resetTour,
        getResumeData,
        clearResumeData
    };

    return (
        <AIAvatarContext.Provider value={value}>
            {children}
        </AIAvatarContext.Provider>
    );
};

export default AIAvatarContext;
